import React, { useEffect, useState } from "react";
import { useTranslation } from "@me8eon/translation";
import { useJwtToken } from "@me8eon/react_authentication";
import { listIncidents } from "../../services/incidentsApi";
import { listOrders } from "../../services/ordersApi";
import { listItResources } from "../../services/itResourcesApi";
import { EonUiButton } from "@eon-ui/eon-ui-components-react";
import LoaderComponent from "../../components/loader-component/LoaderComponent";
import TableComponent from "../../components/generic-components/data-table/TableComponent";
import "./ServiceRequest.scss";
import { EnvironmentConfig } from "@me8eon/config";
import { isLocalHost } from "../../util/common-util";
import tableHeaderLabel from "./tableHeaderLabel.json";
import { getFormattedDateTime } from "@me8eon/time";

export const serviceRequestTypes = {
    incidents: {
        url: `${EnvironmentConfig.serviceUrl}/itsm-tablequery-api/v1/list/inc/byKid?isActive=true`,
        closedUrl: `${EnvironmentConfig.serviceUrl}/itsm-tablequery-api/v1/list/inc/byKid?isActive=false`,
        redirectUrl: null,
        errorMessage: "snow.tabName.incidentsTable.noData",
        buttonText: "snow.tabName.incidentsTable.btnText",
        buttonTextOngoing: "snow.tabName.incidentsTable.ongoing",
        buttonTextClosed: "snow.tabName.incidentsTable.closed",
        headlineText: "snow.tabName.incidentsTable.headlineText",
        tooltipText: ""
    },
    itResources: {
        url: `${EnvironmentConfig.serviceUrl}/itsm-tablequery-api/v1/list/asset/byKid?isActive=true`,
        closedUrl: null,
        redirectUrl: null,
        errorMessage: "snow.tabName.itResourcesTable.noData",
        buttonText: null,
        buttonTextOngoing: null,
        buttonTextClosed: null,
        headlineText: "snow.tabName.itResourcesTable.headlineText",
        tooltipText: ""
    },
    orders: {
        url: `${EnvironmentConfig.serviceUrl}/itsm-tablequery-api/v1/list/ritm/byKid?isActive=true`,
        closedUrl: null,
        redirectUrl: "https://orderit.eon.com/orderit?id=order_it",
        errorMessage: "snow.tabName.orderTable.noData",
        buttonText: "snow.tabName.orderTable.btnText",
        buttonTextOngoing: null,
        buttonTextClosed: null,
        headlineText: "snow.tabName.orderTable.headlineText",
        tooltipText: "snow.tabName.orderTable.tooltipText"
    },
};

interface ServiceRequestPageProps {
    type: keyof typeof serviceRequestTypes;
}
type tableDataType = {
    [key: string]: string;
};
type tableDataLang = {
    [key: string]: tableDataType;
};
function ServiceRequestPage({ type }: Readonly<ServiceRequestPageProps>) {
    const { redirectUrl, buttonText, buttonTextOngoing, buttonTextClosed, headlineText, tooltipText, errorMessage } =
        serviceRequestTypes[type];
    const [resData, setResData] = useState<any[]>([]);
    const [preLoader, setPreLoader] = useState(false);
    const [cols, setCols] = useState<string[]>([]);
    const [headerMaps, setHeaderMaps] = useState<string[]>([]);
    const [selectedTab, setSelectedTab] = useState<"ongoing" | "closed">("ongoing");
    const { t, i18n } = useTranslation();
    const lang = i18n.language;
    const jwtToken = useJwtToken();

    const tableHeaderData: tableDataLang = tableHeaderLabel;

    useEffect(() => {
        if (resData.length === 0) return;
        const hasClosedAt = resData.some(
            (item) =>
                item.closed_at &&
                (typeof item.closed_at === "string"
                    ? item.closed_at.trim() !== ""
                    : true)
        );
        const hasResolvedAt = resData.some(
            (item) =>
                item.resolved_at &&
                (typeof item.resolved_at === "string"
                    ? item.resolved_at.trim() !== ""
                    : true)
        );
        const filteredKeys = Object.keys(resData[0]).filter(
            (key) =>
                (key !== "closed_at" || hasClosedAt) &&
                (key !== "resolved_at" || hasResolvedAt)
        );

        const columns = filteredKeys.map(key => tableHeaderData[lang][key]);
        setCols(columns);
        setHeaderMaps(filteredKeys);

    }, [resData, lang]);

    useEffect(() => {
        getOnloadAPIData(true);
    }, []);

    const handleTabChange = (tab: "ongoing" | "closed") => {
        setSelectedTab(tab);
        getOnloadAPIData(tab === "ongoing");
    }

    const getOnloadAPIData = async (isActive: boolean) => {
        try {
            setPreLoader(true);
            let queryResult: any[];
            if (type === "incidents") {
                const body = await listIncidents(isActive, jwtToken);
                queryResult = body?.result?.queryResult ?? [];
            } else if (type === "orders") {
                const body = await listOrders(isActive, jwtToken);
                queryResult = body?.result?.queryResult ?? [];
            } else {
                const body = await listItResources(isActive, jwtToken);
                queryResult = body?.result?.queryResult ?? [];
            }

            if (queryResult.length == 0 && isLocalHost()) {
                queryResult.push(
                    {
                        model_category: "Google",
                        portalURL: "https://www.google.com",
                    },
                    {
                        model_category: "HCL Tech",
                        portalURL: "https://www.hcltech.com",
                    });

            }

            if (!isActive) {
                queryResult = queryResult.filter(
                    result => result.state && result.state == 'Closed'
                );
            }
            const getCustomDateTime = (str: string) => {
                if(!str || str.trim() === "") {
                    return " ";
                }
                let dateTimeArray = 
                    getFormattedDateTime(
                              new Date(str),
                              "d MMM Y  h:i A",
                              true
                          )
                .split(" ").filter((item) => item.trim() !== "");
                if (dateTimeArray.length > 0) {
                    return (
                        <>
                            <span className="date-time-one-line">
                                {`${dateTimeArray[0]} ${dateTimeArray[1]} ${dateTimeArray[2]}`}
                            </span>
                            <span className="date-time-one-line">
                                {`${dateTimeArray[3]} ${dateTimeArray[4]}`}
                            </span>
                        </>
                    );
                }
                return " ";
            };
            const results = queryResult.map((result: any) => {
                const formatted = {
                    ...result,
                    opened_at: getCustomDateTime(result.opened_at),
                    closed_at: getCustomDateTime(result.closed_at),
                    resolved_at: getCustomDateTime(result.resolved_at),
                };

                if (!isActive) {
                    const {
                        u_service_value_2,
                        asset_tag,
                        cat_item_sys_id,
                        // resolved_at,
                        ...rest
                    } = formatted;
                    return rest;
                } else {
                    const {
                        u_service_value_2,
                        asset_tag,
                        cat_item_sys_id,
                        closed_at,
                        ...rest
                    } = formatted;
                    return rest;
                }
            });
            setResData(results);
        } catch (error) {
            console.error(`Service Request: ${type}`, error);
        } finally {
            setPreLoader(false);
        }
    };

    return (
        <div className="child-container" data-testid="serviceRequestRoot">
            <div className="headline-button-container">

                <div className="child-app-headline">
                    <span className="child-app-headline-text" >
                        {t(headlineText)}
                    </span>

                </div>
                {redirectUrl && (
                    <div className="child-ticket-add">
                        <EonUiButton
                            data-testid="addButton"
                            onClick={() => window.open(redirectUrl, "_blank")}
                            icon-position={"left"}
                            icon={"add"}
                            text={t(buttonText)}
                            size="small"
                            title={t(tooltipText)}
                        />
                    </div>
                )}

            </div>

            <div className="child-container-table">
                {type === "incidents" && (<div className="tab-wrapper" style={{ marginTop: "0px" }}>
                    <div className="tab-scroller">
                        <button className={selectedTab === "ongoing" ? "tab tab--active" : "tab"}
                            onClick={() => handleTabChange("ongoing")}>
                            {t(buttonTextOngoing ?? "")}
                        </button>
                        <button className={selectedTab === "closed" ? "tab tab--active" : "tab"}
                            onClick={() => handleTabChange("closed")}>
                            {t(buttonTextClosed ?? "")}
                        </button>
                    </div>
                </div>)}
                {preLoader && (
                    <div className="loader-container" data-testid="loader">
                        <LoaderComponent isLoading isLinear />
                    </div>
                )}
                {!preLoader && resData.length ? (
                    <TableComponent rows={resData} cols={cols} headerMaps={headerMaps} showPagination={true} />
                ) : (
                    <div className="child-no-data-message">
                        <p>{t(errorMessage)}</p>
                    </div>
                )}
            </div>
        </div>
    );
}

export default ServiceRequestPage;
